import type { QueryResult } from "@apollo/client";
import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { GET_ORGANIZATION_STAKEHOLDERS } from "./queries";
import type { IStakeholderDataSet } from "./types";

import type {
  Exact,
  GetOrganizationStakeholdersQuery,
  Scalars,
} from "gql/graphql";
import { useAudit } from "hooks/use-audit";
import { Logger } from "utils/logger";
import { msgError } from "utils/notifications";

type TQueryResult = QueryResult<
  GetOrganizationStakeholdersQuery,
  Exact<{ organizationId: Scalars["String"]["input"] }>
>;

interface IUseOrgMembersResponse extends Omit<TQueryResult, "data"> {
  data: IStakeholderDataSet[];
}

const useOrganizationStakeholdersQuery = (
  organizationId: string,
): IUseOrgMembersResponse => {
  const { addAuditEvent } = useAudit();
  const { t } = useTranslation();

  const { data, loading, refetch, ...rest } = useQuery(
    GET_ORGANIZATION_STAKEHOLDERS,
    {
      onCompleted: (): void => {
        addAuditEvent("Organization.Stakeholders", organizationId);
      },
      onError: ({ graphQLErrors }): void => {
        graphQLErrors.forEach((error): void => {
          msgError(t("groupAlerts.errorTextsad"));
          Logger.warning(
            "An error occurred fetching organization members",
            error,
          );
        });
      },
      variables: { organizationId },
    },
  );

  const stakeholdersList = useMemo(
    (): IStakeholderDataSet[] =>
      (data?.organization.stakeholders ??
        []) as unknown as IStakeholderDataSet[],
    [data?.organization.stakeholders],
  );

  return {
    data: stakeholdersList,
    loading,
    refetch,
    ...rest,
  };
};

export { useOrganizationStakeholdersQuery };
